import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { DatePicker } from "antd";
import moment from "moment";

import K from "utilities/constants";

const DATE_FORMATS = [
  K.DateFormat.DashUSFormat,
  K.DateFormat.USFormat,
  K.DateFormat.ShortUSFormat,
  K.DateFormat.Response,
  "MM-DD-YY",
];

const toMoment = (raw) => {
  if (!raw) return null;
  const parsed = moment(raw, DATE_FORMATS, true);
  return parsed.isValid() ? parsed : null;
};

export const DatePickerEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState(() => toMoment(props.value));
  const [open, setOpen] = useState(true);
  const containerRef = useRef(null);
  const committedRef = useRef(null);

  const getInputEl = () => containerRef.current?.querySelector("input");

  useEffect(() => {
    getInputEl()?.focus();
  }, []);

  const commitValue = (raw) => {
    const text = `${raw ?? ""}`.trim();
    const parsed = toMoment(text);
    committedRef.current = parsed
      ? parsed.format(K.DateFormat.DashUSFormat)
      : text;
  };

  useImperativeHandle(ref, () => ({
    getValue: () => {
      if (committedRef.current !== null) return committedRef.current;
      return getInputEl()?.value?.trim() ?? "";
    },
  }));

  const handleChange = (date) => {
    setValue(date);
    commitValue(date ? date.format(K.DateFormat.DashUSFormat) : "");
    setOpen(false);
    props.stopEditing?.();
  };

  const handleKeyDownCapture = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    event.stopPropagation();

    commitValue(getInputEl()?.value);
    setOpen(false);
    props.stopEditing?.();
  };

  return (
    <div
      ref={containerRef}
      style={{ width: "100%", height: "100%", display: "flex" }}
      onKeyDownCapture={handleKeyDownCapture}
    >
      <DatePicker
        value={value}
        open={open}
        onOpenChange={setOpen}
        allowClear
        bordered={false}
        format={K.DateFormat.DashUSFormat}
        onChange={handleChange}
        inputReadOnly={false}
        getPopupContainer={() => document.body}
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
});
